import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const benefits = [
    {
        emoji: '💰',
        title: 'Ganar Más Dinero',
        description: 'Sueldos +50% más altos que roles tradicionales',
        gradient: 'from-green-900 via-emerald-800 to-teal-900',
    },
    {
        emoji: '⏰',
        title: 'Ganar Tiempo',
        description: 'Trabajo remoto, menos viajes, más vida',
        image: '/images/remando.jfif',
    },
    {
        emoji: '🗽',
        title: 'Ganar Libertad',
        description: 'La libertad de trabajar desde mi casa recuperando horas de vida usándolas en lo que quiera',
        image: '/images/foto_home_office.jfif',
    },
];

export default function WhyDataEngineering() {
    const [activeIndex, setActiveIndex] = useState(0);
    const activeBenefit = benefits[activeIndex];

    // Auto-rotate carousel every 4 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % benefits.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    return (
        <section className="py-24">
            <div className="max-w-7xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    {/* Header */}
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-green-400 mb-6">
                            ¿Por qué Data Engineering?
                        </h2>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Data Engineering = Ganar más dinero + Ganar tiempo + Ganar libertad
                        </p>
                        <div className="w-20 h-1 bg-green-500 mx-auto mt-6"></div>
                    </div>

                    {/* Featured Benefit Card */}
                    <div className="relative max-w-5xl mx-auto">
                        <motion.div
                            key={activeIndex}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl"
                        >
                            <div className="absolute inset-0">
                                {activeBenefit.image ? (
                                    <>
                                        <img
                                            src={activeBenefit.image}
                                            alt={activeBenefit.title}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                                    </>
                                ) : (
                                    <div className={`w-full h-full bg-gradient-to-br ${activeBenefit.gradient}`} />
                                )}
                            </div>
                            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12">
                                <div className="text-6xl md:text-7xl mb-4">{activeBenefit.emoji}</div>
                                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">{activeBenefit.title}</h3>
                                <p className="text-xl md:text-2xl text-white leading-relaxed max-w-3xl">{activeBenefit.description}</p>
                            </div>
                        </motion.div>

                        {/* Carousel dots */}
                        <div className="flex justify-center gap-2 mt-6">
                            {benefits.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveIndex(index)}
                                    className={`h-2 rounded-full transition-all duration-300 ${index === activeIndex ? 'w-8 bg-green-500' : 'w-2 bg-gray-600 hover:bg-gray-500'
                                        }`}
                                    aria-label={`Ir al beneficio ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Salary Section */}
                    <div className="mt-20 max-w-4xl mx-auto">
                        <div className="text-center mb-8">
                            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                Los números hablan por sí solos
                            </h3>
                            <p className="text-xl text-gray-300">Sueldos reales del mercado en 2026</p>
                        </div>
                        <div className="bg-gray-900/70 rounded-3xl p-8 shadow-lg border border-green-500/20">
                            <img
                                src="/images/Sueldos_data_2026.png"
                                alt="Sueldos Ingenieros de Datos 2026"
                                className="w-full rounded-2xl shadow-sm"
                            />
                            <div className="mt-8 text-center space-y-4">
                                <p className="text-2xl md:text-3xl font-bold text-white">
                                    ¿Vale la pena invertir en tu formación?
                                </p>
                                <p className="text-xl text-gray-300 leading-relaxed">
                                    Con tu primer mes de sueldo como Data Engineer recuperás
                                    <span className="text-green-400 font-bold"> múltiples veces </span>
                                    la inversión en mentoría.
                                </p>
                                <p className="text-lg text-gray-300 italic">
                                    La pregunta no es si podés permitirte una mentoría.<br />
                                    <strong className="text-white">La pregunta es: ¿Cuánto tiempo más vas a seguir soportando lo mismo?</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
